import { IFWPauseAdGlobalParams } from 'tag/ts/tag/interfaces/ads/pause/IFWPauseAdGlobalParams';
import { IFWPauseAdSTVParams } from 'tag/ts/tag/interfaces/ads/pause/IFWPauseAdSTVParams';
import { ITag } from 'tag/ts/tag/interfaces/ITag';

/**
 * @copyright © Paramount Streaming
 * @description Freewheel custom parameters model.
 */
export class FWPauseAdSTVParams {

	readonly #params: IFWPauseAdSTVParams;
	readonly #paramsOverride: IFWPauseAdGlobalParams = {
		asnw: '',
		caid: '',
		csid: '',
		flag: '',
		metr: '',
		nw: '',
		prof: '',
		pvrn: '',
		resp: '',
		ssnw: '',
		vprn: ''
	};
	readonly #excludedParams: string[] = [
		'nextShowsIdsArray',
		'ifa',
		'cust_params'
	];

	constructor( tag: ITag ) {
		const utils = window.SmartTag.utilities;
		const prefix = 'imafw_';
		const html5Params = tag?.params?.CONTENT?.resource?.ad?.ssai?.adParameters;
		const smartTagParams = tag?.controller?.playerManager?.currentResource?.ad?.ssai?.adParameters;
		const validParams = utils.Type.isObject( html5Params ) && !utils.Type.isEmpty( html5Params )
			? html5Params
			: smartTagParams;
		this.#params = {
			...validParams
		} as IFWPauseAdSTVParams;
		// Remove params not needed for pause ad
		this.#excludedParams.forEach( ( key ) => {
			if (
				this.#params?.[ key ]
				&&
				!utils.Type.isEmpty( this.#params[ key ] )
			) {
				delete this.#params[ key ];
			}
		} );
		// Save Params for override and remove them from the general STV params
		Object.entries( this.#paramsOverride ).forEach( ( [ key, value ] ) => {
			// Search for keys to override
			if (
				this.#params?.[ key ]
				&&
				!utils.Type.isEmpty( this.#params[ key ] )
			) {
				this.#paramsOverride[ key ] = this.#params[ key ];
				delete this.#params[ key ];
			}
			// Search for prefixed keys to override
			const prefixedKey: string = prefix + key;
			if (
				this.#params?.[ prefixedKey ]
				&&
				!utils.Type.isEmpty( this.#params[ prefixedKey ] )
			) {
				this.#paramsOverride[ key ] = this.#params[ prefixedKey ];
				delete this.#params[ prefixedKey ];
			}
		} );
		// https://paramountplus.atlassian.net/browse/PLSTVVID-494
		// Combine 'rdid' & 'idtype' into new key: '_fw_did', if it doesn't exist already
		// for LAT ON case, omit _fw_did entirely
		const params = this.#params as unknown as Record<string, unknown>;
		const ctvLatValue =
			params?.[ 'imafw_is_lat' ] ||
			params?.[ 'is_lat' ] ||
			params?.[ 'imafw__fw_is_lat' ];
		const latEnabled: boolean = String( ctvLatValue ) === '1';

		const didKey: string = '_fw_did';
		const prefixedDidKey: string = prefix + didKey;
		if (
			(
				!latEnabled
			)
			&&
			(
				!this.#params?.[ didKey ] || utils.Type.isEmpty( this.#params?.[ didKey ] )
			)
			&&
			(
				!this.#params?.[ prefixedDidKey ] || utils.Type.isEmpty( this.#params?.[ prefixedDidKey ] )
			)
		) {
			const rdid: string = this.#params?.[ 'rdid' ] || this.#params?.[ prefix + 'rdid' ];
			const idtype: string = this.#params?.[ 'idtype' ] || this.#params?.[ prefix + 'idtype' ];
			this.#params[ '_fw_did' ] = `${idtype}:${rdid}`;
		}
	}

	/**
	 * @description Returns STV global parameters override
	 * @returns {IFWPauseAdGlobalParams}
	 */
	get paramsOverride(): IFWPauseAdGlobalParams {
		return this.#paramsOverride;
	}

	/**
	 * @description Returns STV parameters
	 * @returns {IFWPauseAdSTVParams}
	 */
	get params(): IFWPauseAdSTVParams {
		return this.#params;
	}
}